'use client';

import { Button } from '@askable/ui/core/button';
import { toast } from '@askable/ui/core/sonner';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'urql';

import ErrorView from '@/components/Error/Error';
import { MainLayout } from '@/components/Layouts/MainLayout';
import { useViewerContext } from '@/context/ViewerContext/ViewerContext';
import Header from '@/src/app/components/Header';

import { CreateMeeting } from '../data/CreateMeeting.query';

/**
 * Home Page
 * Action to start a new call
 */

const HomePage = () => {
  const [{ fetching, error }, createAskableVideoMeeting] = useMutation(CreateMeeting);
  const { details } = useViewerContext();
  const { t } = useTranslation();
  const router = useRouter();

  const startNewCall = async () => {
    router.prefetch('/rooms/[id]');

    const response = await createAskableVideoMeeting({
      name: t('askable.booking.type.remote'),
    });

    if (error || !response.data?.createAskableVideoMeeting?.code) {
      toast.error(error?.message ?? t('global.missingRoomCode'));

      return;
    }

    router.push(`/rooms/${response.data.createAskableVideoMeeting.code}`);
  };

  if (error) {
    return (
      <MainLayout>
        <ErrorView />
      </MainLayout>
    );
  }

  return (
    <div className="flex h-full w-full flex-col bg-background text-foreground">
      <Header />

      <main className="flex flex-grow items-center justify-center sm:container">
        <div className="grid grid-cols-1 items-center gap-6 p-2 text-center sm:grid-cols-2 sm:p-4 sm:text-left xl:gap-16">
          <header className="flex max-w-xl flex-col gap-4 text-center sm:flex-shrink-0 sm:text-left">
            <div className="flex w-full flex-col gap-2">
              <h1 className="text-balance text-3xl font-bold sm:text-5xl">{t('sections.home.title')}</h1>
              <p className="text-pretty text-md font-medium sm:text-xl">{t('sections.home.description')}</p>
            </div>

            {details?.type?.client ? (
              <Button
                className="m-auto sm:m-0"
                isLoading={fetching}
                onClick={() => void startNewCall()}
                size="lg"
                variant="primary"
              >
                {t('sections.home.newCall')}
              </Button>
            ) : null}
          </header>

          <div className="flex flex-col items-center text-center">
            <Image src="/askable-sessions-landing-dark.png" alt="" width="400" height="400" className="m-auto" />
            <div className="-mt-10 flex max-w-md flex-col gap-2">
              <div className="text-balance text-lg font-semibold">{t('sections.home.outOfTheBoxTitle')}</div>
              <div className="text-balance">{t('sections.home.outOfTheBoxDescription')}</div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default HomePage;
